import { invertObj, pick, toLower, toUpper } from 'ramda';

export class LanguageService {
    NGMLocalesMapping = {
        ZH: 'zh-cn',
        ZH_CN: 'zh-cn'
    };

    /*
    * // 'en_GB'
    * getLanguage('en_GB');
    * // 'en_US'
    * getLanguage('en'); if 'en_US' is first en-language from Playtech.Variables.allowedLocales except defaultLocale;
    * // 'it_IT'
    * getLanguage('it');
    * // 'en_CA'
    * getLanguage('al') => 'en_CA'; if 'al' is aliases for 'en_CA';
    * // 'zh_TW'
    * getLanguage('zh-tw'); see window.Playtech.Data.imsLocalesMapping (if zh_TW is in allowedLocales);
    * */
    getLanguage = (lang) => window.Playtech.Methods.getLanguage(lang);

    getAliasForLanguage = (lang) => this.aliasLocaleMapping[lang] || lang;

    getLanguageForAlias = (alias) => this.aliasLocaleMappingInverted[alias];

    /*
    * // undefined
    * getShortLanguage('en');
    * // 'en_AU'
    * getShortLanguage('en_AU'); if 'en_AU' is not first en-language from Playtech.Variables.allowedLocales except defaultLocale;
    * // 'en'
    * getShortLanguage('en_US'); if 'en_US' is first en-language from Playtech.Variables.allowedLocales except defaultLocale;
    * // 'it'
    * getShortLanguage('it_IT');
    * // 'pt'
    * getShortLanguage('pt_PT'); if 'pt_PT' is first pt-language from Playtech.Variables.allowedLocales;
    * // 'pt_BR'
    * getShortLanguage('pt_BR');
    * */
    getShortLanguage = (lang) => this.shortLocalesMapping[lang];

    getNGMLanguage = (lang) => this.NGMLocalesMapping[toUpper(lang)] || this.getTwoLetterLanguage(lang);

    convertFromIMS = (lang) => this.imsLocalesMappingInverted[lang] || this.defaultLocale;

    getImsLocalesMapping = (allowedLocales) => pick(allowedLocales, window.Playtech.Data.imsLocaleMapping);

    /**
     * Retrieve current 2-symbols language
     * @param {string} [lang]
     * @returns {string}
     */
    getTwoLetterLanguage = (lang) => {
        const language = this.getLanguage(lang);

        return language ? language.slice(0, 2) : null;
    };

    convertToIMS = (lang) => {
        let validLang = lang;

        if (!this.allowedLocales.includes(validLang)) {
            validLang = this.shortLocalesMappingInverted[validLang];
        }

        return this.imsLocalesMapping[validLang] || this.getTwoLetterLanguage(validLang);
    };

    getShortLocaleMapping = (locales = []) =>
        locales.reduce((shortLocales, locale) => {
            const [shortLocale] = locale.split(/[_-]/);

            shortLocales[locale] = invertObj(shortLocales)[shortLocale] ? locale : shortLocale;

            return shortLocales;
        }, {});

    /**
     * Get pathLang:
     * aligned with "pathLang" router parameter;
     * combination of 'lang' hooks.
     * @function
     * @param lang {string | null} language in 5-symbols format
     * @returns {string}
     * @example
     * // ""
     * getPathLang('en_GB') if "en_GB" is default language
     * // "brazil-pt"
     * getPathLang('pt_BR') if "pt_BR" has configured alias "brazil-pt"
     * // "pt"
     * getPathLang('pt_BR') if allowedLocales does not have language with starting "pt" before "pt_BR": ['en_GB', 'pt_BR', 'de_DE']
     * // "pt_BR"
     * getPathLang('pt_BR') if allowedLocales has language with starting "pt" before "pt_BR": ['en_GB', 'pt_PT', 'pt_BR', 'de_DE'];
     * in this case "pt" pathLang is reserved for "pt_PT" language
     */
    getPathLang = (lang) => {
        if (lang === this.defaultLocale && this.getLanguage(lang) === this.defaultLocale) {
            return '';
        }

        const pathLangAlias = this.getAliasForLanguage(lang);

        return pathLangAlias === lang ? this.getShortLanguage(lang) : pathLangAlias;
    };

    // todo this should not be taken directly from Variables.

    get defaultLocale() {
        return window.Playtech?.Variables?.defaultLocale || '';
    }

    get allowedLocales() {
        return window.Playtech?.Variables?.allowedLocales || [];
    }

    get allowedLocalesLowerCased() {
        return this.allowedLocales.map(toLower);
    }

    get aliasLocaleMapping() {
        const aliasLocaleMapping = window.Playtech?.Variables?.aliasLocaleMapping || {};

        return pick(this.allowedLocales, aliasLocaleMapping);
    }

    get aliasLocaleMappingInverted() {
        return invertObj(this.aliasLocaleMapping);
    }

    get imsLocalesMapping() {
        return this.getImsLocalesMapping(this.allowedLocales);
    }

    get imsLocalesMappingInverted() {
        return invertObj(this.imsLocalesMapping);
    }

    get shortLocalesMapping() {
        return this.getShortLocaleMapping(this.allowedLocales);
    }

    get shortLocalesMappingInverted() {
        return invertObj(this.shortLocalesMapping);
    }
}

const languageService = new LanguageService();

export { languageService };
